/**
 * Application Color Theme
 *
 * Centralized color definitions for consistent styling across the app.
 * Use these constants instead of hardcoded hex values so one change updates every screen.
 *
 * @example
 * import { colors } from '../theme/colors';
 * style={{ backgroundColor: colors.primary }}
 */

/**
 * --------------------------------------------------------------------------
 * Plain-English: why some colors here do NOT match the newest Figma file 1:1
 * --------------------------------------------------------------------------
 * Think of the app as having **two paint buckets** we dipped from over time:
 *
 * 1. **Legacy forms** — What Create Request and older screens already shipped with:
 *    a warmer orange (`#FF6B35`), softer greys for paragraphs (`#333` / `#666`), light borders
 *    (`#ddd`), and placeholders that read like “hint grey” (`#999`). Users are used to this.
 *
 * 2. **Visual Template (Vertiv Figma)** — A newer system with different choices (for example
 *    darker neutral strokes or blue focus halos on some controls). We still use those tokens in
 *    places that were built directly against template specs (checkbox/radio rings, some surfaces).
 *
 * **Why we sometimes choose legacy over template for form fields:** shipping a half-migrated
 * palette makes the UI feel broken—labels jump to harsh black, borders jump to navy-grey, etc.
 * So we pointed **brand + core text + basic input chrome** back at the legacy bucket for form
 * parity, while keeping safety greens/reds/blues and newer component chrome where they already
 * matched QA. Product/design can flip everything to strict template in one coordinated pass later.
 *
 * **If you edit a hex:** update `src/theme/__tests__/colors.test.ts` and search the repo for the
 * old value so stories, PDFs, and screenshots do not disagree.
 * --------------------------------------------------------------------------
 *
 * Visual Template file reference (`nzRrn94pPJjuXdhfsuRMN6`) — foundations `2:2`, cards `4054:3307`,
 * typography `881:7557`, grid `7083:29970`. Status greens/reds/blues still follow template utility swatches.
 */
export const colors = {
  /**
   * Brand orange — legacy accent (slightly warmer than template `#FF6600`).
   * Used for primary buttons, focus rings, and “you are here” emphasis on fields.
   */
  primary: '#FF6B35',
  /** Hover / pressed variant of brand — slightly darker so buttons feel clickable. */
  primaryDark: '#E55A2B',
  /**
   * Brand orange at TEXT contrast on white (#1069 Story 7.1). **5.18:1 on
   * `backgroundPrimary`** — clears SC 1.4.3's 4.5:1 as text and SC 1.4.11's
   * 3:1 as a state indicator, so one token can carry a brand-coloured label,
   * its icon and the rule underneath it. Pinned by
   * `src/theme/__tests__/colors.test.ts` through `./contrast.ts`.
   *
   * ⚠️ THERE ARE THREE ORANGES NOW AND THEY ARE NOT INTERCHANGEABLE. Pick by
   * what the colour has to survive:
   *   · `primary` `#FF6B35` — **2.84:1**. Buttons and focus rings, where the
   *     orange is a FILL behind white text and owes nothing itself. As text or
   *     as an indicator it fails both thresholds.
   *   · `primaryDark` `#E55A2B` — **3.60:1**. Means "hover / pressed", and
   *     that meaning is the reason it is not reused here: it would clear 3:1
   *     for a shape but still fails 4.5:1 for a label, and overloading it
   *     would make two states one token.
   *   · `primaryStrong` `#C2410C` — **5.18:1**. Brand-coloured TEXT and state
   *     indicators on white.
   */
  primaryStrong: '#C2410C',

  // Status colors (template utility 6)
  success: '#34C759',
  error: '#FF3B30',
  /**
   * The "New" unread badge's ground (#1069 P3.3). A DARKER RED THAN `error`,
   * and the darkness is the whole reason it is a separate token.
   *
   * Measured, not assumed (WCAG 2.1, sRGB):
   *   · `#fff` on `error` `#FF3B30` = **3.55:1** — FAILS SC 1.4.3 (4.5:1).
   *   · `textPrimary` `#333` on `error` = 3.56:1 — also fails.
   *   · `error` as TEXT on white = 3.55:1 — inverting does not rescue it.
   *   · `#fff` on `#D93228` = **4.73:1** — passes SC 1.4.3.
   * And as a SHAPE (SC 1.4.11, 3:1): `#D93228` is 4.73:1 on
   * `backgroundPrimary` and 4.49:1 on `surfaceHover`. (⚠️ THE TAB HALF OF THIS
   * DERIVATION WAS RETIRED BY #1069 STORY 7.1. The active space tab sat on
   * `surfaceHover` until `spec-panel-top-edge-cleanup` removed the fill, then
   * carried an orange underline, then a `surfaceMessage` fill from
   * 2026-09-08 — which made the badge on a SELECTED tab the 3.87:1 case.
   * Story 7.1 removed that fill too, so on a tab the badge's ground is now
   * **white in both states, 4.73:1**.)
   *
   * ⚠️ THE FLOOR MOVED IN #1069 STORY 5.1, AND THIS COMMENT USED TO STATE THE
   * OLD ONE. It said 4.49:1 was "the floor for any `surfaceHover` ground";
   * repainting the panel gave the badge two new grounds, and the lowest is now
   * **3.87:1 on `surfaceMessage`** (4.25:1 on `surfacePanelMuted`). Still clears
   * the 3:1 that a shape owes, with less room than before. That floor SURVIVES
   * Story 7.1 AND Story 8.4 (2026-09-21): 7.1 took the tab off
   * `surfaceMessage`, and 8.4 (D5) took the unread thread avatar off it too,
   * so no badge SITS on this fill any more. 3.87 is kept as the panel's
   * lowest-luminance fill, the number a future badge on an own-side bubble
   * would meet, not because one sits there today. Unlike every figure above,
   * that one is pinned by a test — `src/theme/__tests__/colors.test.ts`
   * computes it through `./contrast.ts` rather than trusting this paragraph.
   *
   * ⚠️ THE DOT KEEPS `error`, DELIBERATELY. A dot carries no text, so it only
   * owes 3:1, which `error` clears against `UnreadDot`'s white ring. Only the
   * badge has a 4.5:1 obligation, so only the badge pays for it. Unifying both
   * on this hue would be strictly better for contrast and would repaint
   * surfaces the owner has already signed off; that is a design call, not a
   * tidy-up.
   */
  unreadBadge: '#D93228',
  warning: '#FFD600',
  info: '#007AFF',

  /**
   * Body text hierarchy — **not** pure black (`#000`) so long form copy is easier on the eyes.
   * Primary = main sentences; secondary = helper lines; muted = “nice to know” / placeholders tier.
   */
  textPrimary: '#333333',
  actionSolidBlack: '#000000',
  textSecondary: '#666666',
  textMuted: '#999999',
  textLight: '#fff',

  // Background colors
  backgroundPrimary: '#fff',
  backgroundSecondary: '#f5f5f5',
  backgroundMuted: '#f8f8f8',
  /**
   * Request toolbar strip — Chapman chrome matches Header + sidebar white
   * (#999 UX-DR7), not the grey canvas behind step cards.
   */
  surfaceRequestToolbar: '#FFFFFF',
  /** Grey canvas behind request step form cards from the web frame prototype. */
  surfaceRequestCanvas: '#F3F4F6',
  /** Table header strip (View All Requests web table) */
  surfaceTableHeader: '#f1f3f5',
  /** Row hover / muted surface */
  surfaceHover: '#f8f9fa',
  /**
   * The ground a request-list row sits on while it still has changes the viewer
   * has NOT seen (#1575 FR-5).
   *
   * ⚠️ IT MARKED THE OPPOSITE STATE FOR ONE DAY, which is why the name is worth
   * reading twice. The approved design put unread on white and read on this
   * shade; the owner moved the shade to the UNREAD side on 2026-10-07 and kept
   * the weight where it always was. So a row with unseen changes is bold ON THE
   * SHADE, and a row you have read is body weight on white.
   *
   * It is the ground, never the text: both states keep {@link textPrimary},
   * because dimming the words as well would read as "disabled" rather than as a
   * state.
   *
   * ⚠️ SHARES ITS VALUE WITH {@link surfaceRequestCanvas}. Same intent (a quiet
   * grey one step off white) for two different surfaces; kept as its own token
   * so the list can be re-tuned without moving the request canvas. Contrast of
   * `textPrimary` on it is pinned in `__tests__/colors.test.ts`.
   */
  surfaceRowListUnread: '#f3f4f6',
  /**
   * The READER'S OWN message bubbles in the discussion panel (#1069 Story 5.1,
   * owner's "selectionblue1"; narrowed to the own side by Story 5.5 D1 and
   * confirmed by Story 8.4 D3, 2026-09-21). Everyone else's bubble is
   * {@link surfacePanelMuted}; the two are 1.10:1 apart, and ownership is
   * still never colour alone (the lean, the tail corner and the `You` label
   * carry it).
   *
   * ⚠️ THE PENDING BUBBLE TAKES THIS FILL TOO, SINCE 8.4 D2/D3. It is the
   * reader's own message; "not sent yet" is the dashed `borderPanel` edge
   * (the ONLY border left in the thread) and the italic `Sending…` line, both
   * structural. The old warning here ("NOT for the unsent pending bubble")
   * dates from when every live bubble had a border and the pending one was
   * told apart by fill and a 0.7 opacity; measured, the opacity put
   * `Sending…` at 2.82:1. The withdrawn TOMBSTONE has had no bubble at all
   * since 5.5 D8, so it takes no fill from anywhere.
   *
   * Teams' light-theme own-message fill since 2026-10-07 (owner, from a Teams
   * screenshot); it was `#daeaff`. It is lighter, so every text contrast
   * measured on it rises.
   */
  surfaceMessage: '#e8ebfa',
  /**
   * Everyone else's message bubble in the discussion thread (2026-10-07, owner,
   * matching Teams' `#f5f5f5`). Split from {@link surfacePanelMuted}, which
   * keeps the panel's quiet bands and avatar discs at `#f1f3f5`. White file
   * rows inside this bubble still sit one tone away from it (8.4 D7).
   */
  surfaceMessageOther: '#f5f5f5',
  /**
   * Two meanings, one hex (#1069 Story 5.1, owner's "bg4"; Story 8.4 D3,
   * 2026-09-21). OUTSIDE the thread it is the discussion panel's quiet band:
   * the membership strip, the single-space note, the editing strip, the files
   * strip. INSIDE the thread it WAS "somebody else" until 2026-10-07,
   * when other people's bubbles moved to {@link surfaceMessageOther} (Teams'
   * `#f5f5f5`); it still marks the panel's bands and avatar discs.
   *
   * ⚠️ AND IT IS THE PANEL'S TWO AVATAR DISCS AGAIN (owner ruling 2026-09-21,
   * superseding Story 8.4 D5 the same day). 8.4 briefly moved a named
   * author's disc onto one of eight hashed hues; the owner reversed that, the
   * palette is deleted, and both discs — 28 on a thread row, 24 beside a
   * message — are this fill with `textSecondary` initials (**5.16:1**) and no
   * border. The disc's own edge against the white panel is 1.11:1, under the
   * 3:1 shape floor, accepted by the same ruling and recorded in the ledger.
   *
   * ⚠️ ATTACHMENT CHIPS TAKE THIS FILL ON THE OWN SIDE ONLY (8.4 D7). A file
   * row sits one tone away from its bubble: muted on the blue own bubble,
   * WHITE (`backgroundPrimary`) inside a muted other-side bubble, because a
   * muted chip on a muted bubble measured 1.00:1 and vanished. The pending
   * bubble no longer takes this fill (8.4 D2) and the tombstone has had no
   * bubble since 5.5 D8.
   *
   * ⚠️ SHARES ITS VALUE WITH {@link surfaceTableHeader} DELIBERATELY. The two
   * have different roles and may diverge; renaming one to the other would reach
   * outside the correspondence feature into the requests table.
   */
  surfacePanelMuted: '#f1f3f5',
  /**
   * The thread avatar's fill (#1069 thread + list discs). Owner asked for a
   * darker disc that "stands out a bit more" (2026-10-07), superseding the
   * `surfacePanelMuted` fill of the 2026-09-21 ruling; still grey, still no
   * hue, still borderless. Measured through `contrast.ts`: **1.76:1** on
   * white and **1.61:1** on `surfaceMessageOther`, up from 1.11 / 1.02. The
   * initials move to `textPrimary`, **7.19:1** here; `textSecondary` would be
   * 3.27:1 and fail.
   *
   * ⚠️ SHARES ITS VALUE WITH {@link borderPanel}: a fill role, not a borrowed
   * border name, so the two may diverge.
   */
  surfaceAvatar: '#bac4d6',
  /**
   * Visual Template Select — selected list row (`--selection/selection-background`, node `8574:33292`).
   */
  surfaceSelectSelected: '#e7effa',
  /**
   * Visual Template — dropdown menu outer chrome (`--background/bg-3`, Select panel wrapper).
   */
  surfaceDropdownMenuChrome: '#f6f8fa',
  /** Multiselect closed-trigger chip fill (light neutral pill). */
  surfaceMultiSelectChip: '#f0f4fb',
  /** Chip outline — aligns with select neutral stroke at low emphasis. */
  borderMultiSelectChip: '#d8dee9',
  /** Selected / highlighted filter chip background */
  surfaceAccentMuted: '#fff8f4',
  /** Default shadow cast (RN shadowColor) */
  shadowBase: '#000',

  // Border colors
  borderLight: '#eee',
  borderMedium: '#ddd',
  borderDark: '#e0e0e0',
  /**
   * The discussion panel's OUTLINE-weight border (#1069 Story 5.1, owner's
   * "border1") — the panel's left edge, its header and tab rules, the message
   * bubbles and the composer's edges. NOT the thread-list separator: that is
   * {@link borderPanelSoft} (Story 2.4, D6), because a rule between every row
   * at outline weight reads as a grid. The conversation cards this once
   * outlined are gone (Story 2.4, D5); the rows are lines in a list now.
   *
   * Cooler and one step darker than {@link borderMedium}, which it replaces
   * inside the panel: measured 1.76:1 on white against `borderMedium`'s 1.36:1,
   * so this is the more legible edge as well as the consistent one.
   *
   * ⚠️ SHARES ITS VALUE WITH {@link inputBorderDisabled} AND
   * {@link textFormDisabled}. Same hex, unrelated roles, and written lowercase
   * so one grep for `#bac4d6` finds all three.
   */
  borderPanel: '#bac4d6',
  /**
   * The SOFT edge for a line that repeats: the thread-list separator (#1069
   * Story 2.4, D6). `borderPanel` is right for a panel's outline and wrong
   * for a rule between every row, where it reads as a grid. 1.35:1 on white
   * and 1.21:1 on `surfacePanelMuted`, so it stays a visible line on both of
   * the striped list's grounds (pinned in `panelUnreadIndicators.test.tsx`).
   * Same hex as {@link borderMultiSelectChip}, a semantic alias rather than a
   * borrowed name.
   */
  borderPanelSoft: '#d8dee9',
  /**
   * Outline around white cards — light enough to separate panels without looking like a harsh grid.
   * Slightly cooler than `borderMedium` so stacked sections feel airy (legacy profile + forms).
   */
  borderCard: '#e9ecef',

  /** Hairline divider under section headers, attachment band (`SubsectionForm`). */
  borderHairline: '#f0f0f0',

  /** Muted horizontal band behind compact attachment lists (`SubsectionForm`). */
  surfaceFormBandMuted: '#fafafa',

  /** Pending / staged file callout surface + text (bootstrap-compatible warning pair). */
  surfaceWarningMuted: '#fff3cd',
  textOnWarningMuted: '#856404',

  /** Muted strip behind collapsible / section headers (UserProfile section header) */
  backgroundSectionHeader: '#f8f9fa',

  /** Scrim behind modal info dialogs */
  overlayBackdrop: 'rgba(0,0,0,0.45)',

  // Semantic aliases for form states
  inputBorder: '#ddd',
  inputBorderError: '#FF3B30',
  /**
   * Figma Visual Template — Text area error stroke (`--critical/normal`, 2px).
   */
  inputBorderCritical: '#FF3B30',
  /** When a field is keyboard-focused we paint a **brand** outline so users see where typing goes. */
  inputBorderFocus: '#FF6B35',
  /**
   * Resting border — familiar light grey so boxes read as “empty input”, not as heavy UI chrome.
   * (Template used a darker neutral; we stayed lighter for continuity with shipped forms.)
   */
  inputBorderNeutral: '#dddddd',
  /** Mouse-hover border — one step darker than resting so hover is noticeable but still neutral. */
  inputBorderInteractive: '#aaaaaa',
  /** Inside fill of inputs — plain white keeps parity with paper-like legacy forms. */
  backgroundForm: '#ffffff',
  /** Figma Text area disabled (`2432:10110`) — `--background/bg--disabled`. */
  backgroundFormDisabled: '#eeeeee',
  /** Figma disabled field stroke — `--neutral/disabled` (`2432:10110`). */
  inputBorderDisabled: '#bac4d6',
  /** Figma disabled value / placeholder tone in textarea (`2432:10110`). */
  textFormDisabled: '#bac4d6',
  /**
   * Placeholder / “Select…” label tone — classic mid-grey so hints sit **below** real values in importance.
   * (We moved away from darker template placeholder grey because it competed with real labels.)
   */
  inputPlaceholder: '#999999',
  /**
   * Web `box-shadow` for keyboard focus on outlined inputs / selects (`:focus` / `:focus-within`).
   */
  /**
   * Soft glow around focused controls on **web** — matches brand orange but semi-transparent so it
   * doesn’t look like a solid sticker. RGBA uses the same hue as `primary`.
   */
  formControlFocusRing: '0 0 0 2px rgba(255, 107, 53, 0.25)',
  /**
   * Figma Visual Template — Radio (`Selection/Selection Primary` + focus layer): blue focus ring on web.
   */
  formRadioFocusRing: '0 0 0 3px rgba(26, 43, 232, 0.22)',
  /** Radio border + inner dot when selected — matches body text for a clean, non-flashy selected state. */
  formRadioSelected: '#333333',
  /**
   * Checkbox fill + border when ticked. Same value and same reasoning as
   * {@link formRadioSelected}: a selected state reads as a statement of fact, not as a call to
   * action, so it takes the body-text neutral rather than the brand orange. Deliberately not
   * pure `#000` — see the note on `textPrimary`.
   */
  formCheckboxSelected: '#333333',
  /**
   * Text-style actions that are not buttons (a "Show comment" style toggle). Blue because it
   * has to read as pressable without a border or a fill to say so.
   */
  textActionLink: '#263cff',
  /**
   * Text-style link inside the discussion panel (#1069 Story 5.1) — today the
   * team strip's Show / Hide control, which used to be brand orange and so
   * competed with the orange the panel spends on audience.
   *
   * ⚠️ SAME ROLE AS {@link textActionLink}, DIFFERENT HEX, AND THAT IS A KNOWN
   * DUPLICATE. Two blues meaning "text action" is the sort of drift Epic 5
   * exists to remove, so it is recorded rather than left to be discovered.
   * It was not resolved in Story 5.1 because `textActionLink`'s only consumer
   * (`RequestDeliverablesEditor`) sits outside that story's file scope.
   * A convergence should run TOWARD this token: `#1a2be8` is already the Visual
   * Template selection blue behind `formRadioFocusRing`, `formRadioFocusTint`
   * and the three checkbox rings, while `#263cff` is a one-off.
   */
  textLink: '#1a2be8',
  /** Native focus halo fill — semi-transparent version of radio selected blue. */
  formRadioFocusTint: 'rgba(26, 43, 232, 0.14)',
  /**
   * Figma Visual Template — Checkbox (`1855:7965` matrix): interaction halos on web (cols hover / focus / pressed).
   */
  formCheckboxHoverRing: '0 0 0 1px rgba(26, 43, 232, 0.2)',
  formCheckboxFocusRing: '0 0 0 2px rgba(26, 43, 232, 0.28)',
  formCheckboxPressedRing: '0 0 0 3px rgba(26, 43, 232, 0.36)',
} as const;

/**
 * Type for color keys - useful for prop typing
 */
export type ColorKey = keyof typeof colors;

export default colors;
